import { JS_WEB_ERROR_CODES } from '@/common/constants/jsWebErrorCodes'
import type { JsWebErrorCode } from '@/common/constants/jsWebErrorCodes'
import type { JsRequest } from '@/services/websocket/types.ts'

interface JsWebClientErrorOptions {
  description?: string
  developerMessage?: string
}

/**
 * Типизированная ошибка клиентской обработки WS js-request.
 * Используется для формирования ответа `__js_web_exception`.
 */
export class JsWebClientError extends Error {
  readonly code: JsWebErrorCode
  readonly description?: string
  readonly developerMessage?: string

  constructor(
    code: JsWebErrorCode = JS_WEB_ERROR_CODES.GENERIC_CLIENT_ERROR,
    message = 'Client websocket request error',
    options: JsWebClientErrorOptions = {},
  ) {
    super(message)
    this.code = code
    this.description = options.description ?? message
    this.developerMessage = options.developerMessage

    Object.setPrototypeOf(this, JsWebClientError.prototype)
  }
}

const buildDeveloperMessage = (
  context: JsRequest,
  extra?: Record<string, unknown>,
): string =>
  JSON.stringify({
    className: context.className != null ? String(context.className).trim() : String(context.class_name ?? '').trim(),
    methodName: context.methodName != null ? String(context.methodName) : String(context.method_name ?? ''),
    objectId: context.objectId ?? context.object_id,
    requestId: context.request_id,
    socketId: context.socketId ?? context.socket_id,
    ...extra,
  })

export function throwUnknownJsRequestHandler(
  context: JsRequest,
  handlerKind: string,
): never {
  const methodName = context.methodName ?? context.method_name
  throw new JsWebClientError(
    JS_WEB_ERROR_CODES.UNKNOWN_HANDLER,
    `Unknown ${handlerKind}: ${String(methodName || 'undefined')}`,
    {
      description: `Метод не поддерживается клиентом (${handlerKind})`,
      developerMessage: buildDeveloperMessage(context, { handlerKind }),
    },
  )
}

export function throwInvalidJsRequestPayload(
  context: JsRequest,
  reason: string,
  extra?: Record<string, unknown>,
): never {
  throw new JsWebClientError(
    JS_WEB_ERROR_CODES.INVALID_REQUEST_PAYLOAD,
    reason,
    {
      description: 'Некорректные параметры js-request',
      developerMessage: buildDeveloperMessage(context, extra),
    },
  )
}

export function throwRuntimeJsRequestError(
  context: JsRequest,
  message: string,
  error?: unknown,
): never {
  throw new JsWebClientError(
    JS_WEB_ERROR_CODES.RUNTIME_ERROR,
    message,
    {
      description: 'Ошибка выполнения js-request на клиенте',
      developerMessage: buildDeveloperMessage(context, {
        cause: error instanceof Error ? error.message : String(error ?? ''),
      }),
    },
  )
}

export function throwGenericJsRequestError(
  context: JsRequest,
  message = 'Client websocket request error',
): never {
  throw new JsWebClientError(
    JS_WEB_ERROR_CODES.GENERIC_CLIENT_ERROR,
    message,
    {
      description: message,
      developerMessage: buildDeveloperMessage(context),
    },
  )
}
